import React, { useState, useEffect, useRef, useMemo } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Popover, Row, Col, Collapse, Tag, Spin, Select, Divider } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import {
	FeatureFilter,
	ProductKeyValuePair,
	ProductFeature,
	ProductSource,
	ProductSearchRequest,
	ProductTypes,
	SelfCatalogFilters,
	SelfCatalogFilterKeys,
	noOfDistributorsIntegrated,
	SortOptions,
	FilterOptions,
	FilterTagData,
	FilterTagType,
	SortingOptions,
	ProductType,
	AmazonFilterProps,
	distributors as DistributorsList,
	Distributor,
	distributorLabel
} from "@/Contracts/Products";
import { searchProducts } from "@/Services/Products";
import "./ItemFiltersMenu.less";
import {
	SupplierRequest,
	SupplierResponse,
	EtilizeLocale,
	Languages,
	ZomentumPSAsArray,
	zomentumPSALabel
} from "@/Contracts";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import {
	capitalizeFirstLetter,
	filterSelectByTextInput
} from "@/Contracts/Utils";
import { useSelector } from "react-redux";
import ZButton from "@atoms/ZButton";
import ZSelect from "@atoms/ZSelect";
import ZTooltip from "@atoms/ZTooltip";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { EIcons } from "@atoms/ZIcon/interface";
import { ZMenu, ZSpin } from "@molecules";
import { ZCheckbox, ZCheckboxGroup, ZIcon, ZTypography } from "@atoms";
import { GlobalState } from "@/Reducers/interface";
import { ItemFiltersMenuProps } from "./interface";
import { useAddOrEditSupplier } from "@/Hooks/Supplier/useAddOrEditSupplier";
import { searchSupplier } from "@/Services/Settings";
import { IOptionProps } from "@atoms/ZSelect/interface";
import debounce from "lodash/debounce";
import { capitalizeAfterUnderscore } from "@/Utils";
import omit from "lodash/omit";
import { AmazonCatalogFilterKeys } from "@/Contracts";
import ZPopover from "@atoms/ZPopover";
import ZImage from "@atoms/ZImage";
import amazonPrimeLogo from "config/assets/Logos/Prime.png";
import useDispatch from "@/Components/Utils/useDispatch";
import UsersActionCreators from "@/Actions/User";
import { EColors } from "@atoms/ZColors/interface";

const { Panel } = Collapse;

const ItemFiltersMenu: React.FC<ItemFiltersMenuProps> = props => {
	const dispatch = useDispatch();
	const {
		isTechDataIntegrated,
		isSynnexIntegrated,
		distributorsIntegrated,
		isIcecatLocaleFR,
		isIcecatLocaleUS,
		isFrenchLanguage,
		amazonAppliedFilters,
		customDistributors,
		connectedSysDistributors
	} = useSelector((state: GlobalState) => {
		return {
			distributorsIntegrated:
				noOfDistributorsIntegrated(
					state.user.userCompany.integrations
				) > 0,
			isTechDataIntegrated:
				!!state.user.userCompany.integrations.tech_data?.connected_user,
			isSynnexIntegrated:
				!!state.user.userCompany.integrations.synnex?.connected_user,
			isIcecatLocaleFR:
				state.user.userCompany.settings.icecat_locale ===
				EtilizeLocale.FR,
			isIcecatLocaleUS:
				state.user.userCompany.settings.icecat_locale ===
				EtilizeLocale.US,
			isFrenchLanguage:
				state.user.userCompany.settings.language === Languages.FRENCH,
			amazonAppliedFilters: state.user.amazonAppliedFilters,
			customDistributors:
				state.user.userCompany.integrations.custom_distributor,
			connectedSysDistributors: DistributorsList.filter(
				distributor =>
					state.user.userCompany.integrations[distributor] !== null &&
					distributor !== Distributor.CustomDistributor
			)
		};
	});
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const [selectedOption, setSelectedOption] = useState(
		FilterOptions.Manufacturers
	);

	const [selectedProductType, setSelectedProductType] = useState<string[]>(
		props.selectedProductType ?? []
	);
	const [selectedManufacturers, setSelectedManufacturers] = useState(
		props.manufacturerFilters
	);
	const [selectedCategories, setSelectedCategories] = useState(
		props.categoryFilters
	);
	const [selectedFeatures, setSelectedFeatures] = useState(
		props.featureFilters
	);

	const [selectedDistributorsV2, setSelectedDistributorsV2] = useState<{
		system: string[];
		custom: string[];
	}>({
		system: props.selectedDistributorsV2?.system ?? [],
		custom: props.selectedDistributorsV2?.custom ?? []
	});

	const [
		shouldFilterByConnectedDistributor,
		setShouldFilterByConnectedDistributor
	] = useState(props.filterByConnectedDistributors);

	const [excludeOutOfStock, setExcludeOutOfStock] = useState(
		props.excludeOutOfStock
	);
	const [excludeDropShipWH, setExcludeDropShipWH] = useState(
		props.excludeDropShipWH
	);

	const [filterTags, setFilterTags] = useState<FilterTagData[]>([]);
	const [filterCount, setFilterCount] = useState<number>(0);
	// self catalogue filters

	const [selfCatalogFilters, setSelfCatalogFilters] =
		useState<SelfCatalogFilters>(
			props.selfCatalogFilters ?? new SelfCatalogFilters()
		);

	// restricts features based on selected manufacturers, categories
	const [featureList, setFeatureList] = useState<ProductFeature[]>(
		props.metaInfo.features
	);

	const [supplierOptions, setSupplierOptions] = useState<IOptionProps[]>([]);
	const [selectedSuppliers, setSelectedSuppliers] = useState<IOptionProps[]>(
		[]
	);

	const amazonFilterProps = props.amazonFilterProps ?? {};

	const [amazonFiltersState, setAmazonFiltersState] =
		useState<AmazonFilterProps>({
			includeOutOfStockProducts:
				amazonFilterProps.includeOutOfStockProducts,
			eligibleForFreeShipping: amazonFilterProps.eligibleForFreeShipping,
			amazonPrimeEligible: amazonFilterProps.amazonPrimeEligible,
			amazonProductCategory: [],
			amazonProductSubCategory: [],
			amazonProductDeliveryDay: []
		});

	const [selectedCustomDistributors, setSelectedCustomDistributors] =
		useState<string[]>(props.customDistributorsFilters ?? []);

	const shouldUpdateFeatureList = useRef<boolean>(false);

	const { getSuppliers, getSupplierById } = useAddOrEditSupplier();
	const isIcecatIntegrationEnabled = useFeatureFlag("icecat_integration");
	const isIcecatEnabledForAll = useFeatureFlag("icecat_enabled_for_all");

	const fetchSelectedSuppliersById = async () => {
		const selectedSupplierIds = selectedSuppliers.map(
			option => option.key
		) as string[];

		const response = await getSupplierById(selectedSupplierIds);
		const options = response?.data.map(option => ({
			name: option.name,
			value: option.id
		}));

		setSupplierOptions(options ?? []);
	};

	useEffect(() => {
		if (!!!selectedSuppliers.length) {
			// Remove the filter key if no suppliers are selected as tags
			const selfCatalogFiltersWithoutSuppliers = omit(
				selfCatalogFilters,
				FilterTagType.SelfSupplier
			);
			setSelfCatalogFilters(selfCatalogFiltersWithoutSuppliers);
		}
	}, [selectedSuppliers]);

	const handleVisibleChange = (visible: boolean) => {
		setVisible(visible);

		if (visible) {
			setSelectedManufacturers(props.manufacturerFilters);
			setSelectedCategories(props.categoryFilters);
			setSelectedFeatures(props.featureFilters);
			setExcludeOutOfStock(props.excludeOutOfStock);
			setExcludeDropShipWH(props.excludeDropShipWH);
			setShouldFilterByConnectedDistributor(
				props.filterByConnectedDistributors
			);
			setSelectedDistributorsV2(
				props.selectedDistributorsV2 ?? { system: [], custom: [] }
			);
			setSelectedOption(FilterOptions.Manufacturers);
			if (props.selectedProductType) {
				setSelectedProductType(props.selectedProductType);
			}
			if (props.selfCatalogFilters) {
				setSelfCatalogFilters(props.selfCatalogFilters);
			}
			setAmazonFiltersState({
				...amazonFiltersState,
				includeOutOfStockProducts:
					amazonFilterProps.includeOutOfStockProducts,
				eligibleForFreeShipping:
					amazonFilterProps.eligibleForFreeShipping,
				amazonPrimeEligible: amazonFilterProps.amazonPrimeEligible,
				amazonProductCategory: amazonFilterProps.amazonProductCategory,
				amazonProductSubCategory:
					amazonFilterProps.amazonProductSubCategory,
				amazonProductDeliveryDay:
					amazonFilterProps.amazonProductDeliveryDay
			});
			setSelectedCustomDistributors(
				props.customDistributorsFilters ?? []
			);
		}
	};

	const updateAppliedFilters = () => {
		const selectedCategory = props.metaInfo.categories.find(
			item => item.value === amazonFiltersState.amazonProductCategory?.[0]
		);
		const selectedSubCategory = props.metaInfo.sub_categories.find(
			item =>
				item.value === amazonFiltersState.amazonProductSubCategory?.[0]
		);
		const selectedAvailability =
			props.metaInfo.amazon_refinements.availability_options.find(
				item =>
					item.value ===
					amazonFiltersState.includeOutOfStockProducts?.[0]
			);
		const appliedFilters = {
			categories: !!selectedCategory
				? [selectedCategory]
				: amazonAppliedFilters?.categories.length
				? amazonAppliedFilters?.categories
				: [],
			sub_categories: !!selectedSubCategory
				? [selectedSubCategory]
				: amazonAppliedFilters?.sub_categories.length
				? amazonAppliedFilters?.sub_categories
				: [],
			availability_options: !!selectedAvailability
				? [selectedAvailability]
				: amazonAppliedFilters?.availability_options.length
				? amazonAppliedFilters?.availability_options
				: []
		};
		dispatch(
			UsersActionCreators.updateAmazonProductAppliedFilter(appliedFilters)
		);
	};

	const onApply = () => {
		setVisible(false);
		handleFiltersClick();
		if (props.currentItemSource === ProductSource.Amazon) {
			updateAppliedFilters();
		}
	};

	const handleFiltersClick = () => {
		props.onApply(
			selectedManufacturers,
			selectedCategories,
			selectedFeatures,
			selectedProductType,
			selfCatalogFilters,
			shouldFilterByConnectedDistributor,
			excludeOutOfStock,
			excludeDropShipWH,
			{ ...amazonFiltersState },
			props.isCDSearchEnhancementEnabled
				? []
				: selectedCustomDistributors,
			props.isCDSearchEnhancementEnabled
				? selectedDistributorsV2
				: { system: [], custom: [] }
		);
	};

	const updateFeatureList = async () => {
		const searchRequest = ProductSearchRequest.constructSearchRequest(
			props.keyWord,
			(isIcecatIntegrationEnabled &&
				(isIcecatLocaleFR || (isIcecatLocaleUS && isFrenchLanguage))) ||
				isIcecatEnabledForAll
				? ProductSource.Icecat
				: ProductSource.Etilize,
			null,
			shouldFilterByConnectedDistributor || excludeOutOfStock,
			shouldFilterByConnectedDistributor,
			[],
			selectedCustomDistributors,
			excludeOutOfStock,
			excludeDropShipWH,
			selectedManufacturers,
			selectedCategories
		);
		try {
			setLoading(true);
			const productsSearchResponse = await searchProducts(searchRequest);
			setFeatureList(productsSearchResponse.meta_info.features);
			const relevantFeaturesSelected = selectedFeatures.filter(feat =>
				productsSearchResponse.meta_info.features.find(
					newFeat => newFeat.value === feat.id
				)
			);
			setSelectedFeatures(relevantFeaturesSelected);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
			shouldUpdateFeatureList.current = false;
		}
	};

	const onMainMenuChange = (key: FilterOptions) => {
		setSelectedOption(key);
		if (shouldUpdateFeatureList.current) {
			handleFiltersClick();
			updateFeatureList();
		}
	};

	const onFilterSelectDeselect = (
		selectedOption: FilterOptions,
		checkedValues: CheckboxValueType[],
		featId?: string
	) => {
		const stringValues = checkedValues.map(a => a.toString());
		if (selectedOption === FilterOptions.Manufacturers) {
			setSelectedManufacturers(stringValues);
		} else if (selectedOption === FilterOptions.Categories) {
			setSelectedCategories(stringValues);
		} else if (featId) {
			const existing = selectedFeatures.find(feat => feat.id === featId);
			if (existing) {
				existing.values = stringValues;
				setSelectedFeatures([...selectedFeatures]);
			} else {
				const newFilter = new FeatureFilter();
				newFilter.id = featId;
				newFilter.values = stringValues;
				setSelectedFeatures([...selectedFeatures, newFilter]);
			}
		}
	};

	const onFeatureSelectDeselect = (
		checkedValues: CheckboxValueType[],
		featId: string
	) => {
		onFilterSelectDeselect(FilterOptions.Features, checkedValues, featId);
	};

	const onTagClose = (
		filterTagType: FilterTagType,
		filterId: string,
		featChildId?: string
	) => {
		if (filterTagType === FilterTagType.CustomDistributor) {
			const updated = selectedCustomDistributors.filter(
				selected => selected !== filterId
			);
			setSelectedCustomDistributors(updated);
			props.onApply(
				selectedManufacturers,
				selectedCategories,
				selectedFeatures,
				selectedProductType,
				selfCatalogFilters,
				shouldFilterByConnectedDistributor,
				excludeOutOfStock,
				excludeDropShipWH,
				{ ...amazonFiltersState },
				updated,
				selectedDistributorsV2,
				false
			);
		} else if (filterTagType === FilterTagType.Manufacturer) {
			const updated = selectedManufacturers.filter(
				selected => selected !== filterId
			);
			setSelectedManufacturers(updated);
			props.onApply(
				updated,
				selectedCategories,
				selectedFeatures,
				selectedProductType,
				selfCatalogFilters,
				shouldFilterByConnectedDistributor,
				excludeOutOfStock,
				excludeDropShipWH,
				{ ...amazonFiltersState },
				selectedCustomDistributors,
				selectedDistributorsV2,
				false
			);
		} else if (filterTagType === FilterTagType.Category) {
			const updated = selectedCategories.filter(
				selected => selected !== filterId
			);
			setSelectedCategories(updated);
			props.onApply(
				selectedManufacturers,
				updated,
				selectedFeatures,
				selectedProductType,
				selfCatalogFilters,
				shouldFilterByConnectedDistributor,
				excludeOutOfStock,
				excludeDropShipWH,
				{ ...amazonFiltersState },
				selectedCustomDistributors,
				selectedDistributorsV2,
				false
			);
		} else if (featChildId || filterTagType === FilterTagType.Feat) {
			const featList = selectedFeatures.filter(
				feat => feat.id === filterId
			);
			if (featList.length > 0) {
				const feat = featList[0];
				feat.values = feat.values.filter(
					selected => selected !== featChildId
				);
				const updated = new Array(...selectedFeatures);
				setSelectedFeatures(updated);
				props.onApply(
					selectedManufacturers,
					selectedCategories,
					updated,
					selectedProductType,
					selfCatalogFilters,
					shouldFilterByConnectedDistributor,
					excludeOutOfStock,
					excludeDropShipWH,
					{ ...amazonFiltersState },
					selectedCustomDistributors,
					selectedDistributorsV2,
					false
				);
			}
		} else if (
			[
				FilterTagType.SelfVendor,
				FilterTagType.SelfSyncSource,
				FilterTagType.SelfProductType,
				FilterTagType.SelfManufacturer,
				FilterTagType.SelfCategory,
				FilterTagType.SelfSupplier
			].indexOf(filterTagType) > -1
		) {
			let currentFilterId = filterId;
			if (filterTagType === FilterTagType.SelfSupplier) {
				currentFilterId = selectedSuppliers.find(
					option => option.name === filterId
				)?.value as string;

				const updatedSelectedSuppliers = selectedSuppliers.filter(
					supplier => supplier.value !== currentFilterId
				);
				setSelectedSuppliers(updatedSelectedSuppliers);
			}

			const updatedArray = selfCatalogFilters?.[filterTagType]?.filter(
				value => value !== currentFilterId
			);
			const updated = {
				...selfCatalogFilters,
				[filterTagType]:
					updatedArray && updatedArray.length > 0
						? [...updatedArray]
						: undefined
			};

			setSelfCatalogFilters(updated);
			props.onApply(
				selectedManufacturers,
				selectedCategories,
				selectedFeatures,
				selectedProductType,
				updated,
				shouldFilterByConnectedDistributor,
				excludeOutOfStock,
				excludeDropShipWH,
				undefined,
				undefined,
				selectedDistributorsV2
			);
		}
	};

	const handleRemoveAllFilters = () => {
		const amazonFilterProps = {
			includeOutOfStockProducts: [],
			eligibleForFreeShipping: false,
			amazonPrimeEligible: false,
			amazonProductCategory: [],
			amazonProductSubCategory: [],
			amazonProductDeliveryDay: []
		};
		props.onApply(
			[],
			[],
			[],
			[],
			new SelfCatalogFilters(),
			false,
			false,
			false,
			amazonFilterProps,
			[],
			{ system: [], custom: [] },
			false
		);
		setSelectedManufacturers([]);
		setSelectedCategories([]);
		setSelectedFeatures([]);
		setSelectedProductType([]);
		setSelectedOption(FilterOptions.Manufacturers);
		setShouldFilterByConnectedDistributor(
			props.filterByConnectedDistributors
		);
		setSelectedDistributorsV2(
			props.selectedDistributorsV2 ?? { system: [], custom: [] }
		);
		setExcludeOutOfStock(props.excludeOutOfStock);
		setExcludeDropShipWH(props.excludeDropShipWH);
		setSelfCatalogFilters(new SelfCatalogFilters());
		setAmazonFiltersState({
			...amazonFiltersState,
			includeOutOfStockProducts: [],
			eligibleForFreeShipping: false,
			amazonPrimeEligible: false,
			amazonProductCategory: [],
			amazonProductSubCategory: [],
			amazonProductDeliveryDay: []
		});
		if (props.currentItemSource === ProductSource.Amazon) {
			const appliedFilters = {
				categories: [],
				sub_categories: [],
				availability_options: []
			};
			dispatch(
				UsersActionCreators.updateAmazonProductAppliedFilter(
					appliedFilters
				)
			);
		}
	};
	const selectedFiltersCount = () => {
		const selectedFeatureOptions = selectedFeatures
			.map(feat => feat.values)
			.reduce((a, b) => a.concat(b), []);
		return (
			selectedManufacturers?.length +
			selectedCategories?.length +
			selectedFeatureOptions?.length
		);
	};

	const updateShouldFilterByConnectedDistributor = (
		e: CheckboxChangeEvent
	) => {
		const checked = e.target.checked;
		setShouldFilterByConnectedDistributor(checked);
		if (!checked) {
			setExcludeOutOfStock(false);
			setExcludeDropShipWH(false);
			if (props.isCDSearchEnhancementEnabled) {
				setSelectedDistributorsV2({ system: [], custom: [] });
			}
		} else if (props.isCDSearchEnhancementEnabled) {
			setSelectedDistributorsV2({
				system: connectedSysDistributors,
				custom:
					customDistributors?.map(distributor => distributor.id) ?? []
			});
		}
		if (props.isCDSearchEnhancementEnabled) {
			onMainMenuChange(FilterOptions.Distributors);
		}
	};

	const updateExcludeOutOfStock = (e: CheckboxChangeEvent) => {
		setExcludeOutOfStock(e.target.checked);
	};

	const updateExcludeDropShipWH = (e: CheckboxChangeEvent) => {
		setExcludeDropShipWH(e.target.checked);
	};

	const excludeOutofStockDisabled =
		!shouldFilterByConnectedDistributor ||
		!(isTechDataIntegrated || isSynnexIntegrated);

	const manufacturerList = useMemo(() => {
		const selected = props.manufacturerFilters
			.map(id =>
				props.metaInfo.manufacturers.filter(item => item.value === id)
			)
			.reduce((a, b) => a.concat(b), []);
		const notSelected = props.metaInfo.manufacturers.filter(
			item => !selected.find(selected => selected.value === item.value)
		);
		const all = [...selected, ...notSelected];
		const withUpdatedCountInLabel = all.map(pair => {
			const newPair = new ProductKeyValuePair();
			newPair.label = `${pair.label} (${pair.count})`;
			newPair.value = pair.value;
			newPair.count = pair.count;
			return newPair;
		});
		return withUpdatedCountInLabel;
	}, [props.metaInfo.manufacturers, props.manufacturerFilters]);
	const categoryList = useMemo(() => {
		const selected = props.categoryFilters
			.map(id =>
				props.metaInfo.categories.filter(item => item.value === id)
			)
			.reduce((a, b) => a.concat(b), []);
		const notSelected = props.metaInfo.categories.filter(
			item => !selected.find(selected => selected.value === item.value)
		);
		const all = [...selected, ...notSelected];
		const withUpdatedCountInLabel = all.map(pair => {
			const newPair = new ProductKeyValuePair();
			newPair.label = `${pair.label} (${pair.count})`;
			newPair.value = pair.value;
			newPair.count = pair.count;
			return newPair;
		});
		return withUpdatedCountInLabel;
	}, [props.metaInfo.categories, props.categoryFilters]);

	useEffect(() => {
		const constructTags = (
			selectedFilters: string[],
			meta: ProductKeyValuePair[],
			type: FilterTagType,
			isSelfFilter?: boolean
		) => {
			const newTags = new Array<FilterTagData>();

			if (isSelfFilter) {
				selectedFilters.forEach(filter => {
					const newFilterTag = new FilterTagData();

					newFilterTag.name =
						filter === ProductSource.Icecat &&
						isIcecatIntegrationEnabled
							? "Global Catalogue"
							: filter === ProductSource.Amazon
							? "Amazon Business"
							: filter;

					newFilterTag.id = filter;
					newFilterTag.type = type;
					newFilterTag.isSelfFilter = true;
					newTags.push(newFilterTag);
				});
			} else {
				selectedFilters.forEach(filter => {
					const selected = meta.find(man => filter === man.value);
					if (selected) {
						const tag = new FilterTagData();
						tag.id = selected.value;
						tag.name = selected.label;
						tag.type = type;
						newTags.push(tag);
					}
				});
			}

			return newTags;
		};

		const constructFeatTags = (selectedFeatures: FeatureFilter[]) => {
			const newTags = new Array<FilterTagData>();
			selectedFeatures.forEach(feat => {
				const metaFeature = featureList.find(
					metaF => metaF.value === feat.id
				);
				if (metaFeature) {
					feat.values.forEach(filter => {
						const selected = metaFeature.children.find(
							m => m.value === filter
						);
						if (selected) {
							const tag = new FilterTagData();
							tag.id = metaFeature.value;
							tag.name = metaFeature.label;
							tag.featChildId = selected.value;
							tag.featChildName = selected.label;
							tag.type = FilterTagType.Feat;
							newTags.push(tag);
						}
					});
				}
			});
			return newTags;
		};

		let newTags = constructTags(
			props.manufacturerFilters,
			props.metaInfo.manufacturers,
			FilterTagType.Manufacturer
		)
			.concat(
				constructTags(
					props.categoryFilters,
					props.metaInfo.categories,
					FilterTagType.Category
				)
			)
			.concat(constructFeatTags(props.featureFilters));

		if (props.selfCatalogFilters?.filter_by_categories) {
			newTags = newTags.concat(
				constructTags(
					props.selfCatalogFilters.filter_by_categories,
					[],
					FilterTagType.SelfCategory,
					true
				)
			);
		}

		if (props.selfCatalogFilters?.filter_by_item_types) {
			newTags = newTags.concat(
				constructTags(
					props.selfCatalogFilters.filter_by_item_types,
					[],
					FilterTagType.SelfProductType,
					true
				)
			);
		}

		if (props.selfCatalogFilters?.filter_by_manufacturers) {
			newTags = newTags.concat(
				constructTags(
					props.selfCatalogFilters.filter_by_manufacturers,
					[],
					FilterTagType.SelfManufacturer,
					true
				)
			);
		}

		if (props.selfCatalogFilters?.filter_by_vendors) {
			newTags = newTags.concat(
				constructTags(
					props.selfCatalogFilters.filter_by_vendors,
					[],
					FilterTagType.SelfVendor,
					true
				)
			);
		}

		if (props.selfCatalogFilters?.filter_by_supplier_ids) {
			const supplierTags =
				props.selfCatalogFilters.filter_by_supplier_ids.map(
					id =>
						selectedSuppliers.find(option => option.value === id)
							?.name as string
				);

			newTags = newTags.concat(
				constructTags(
					supplierTags,
					[],
					FilterTagType.SelfSupplier,
					true
				)
			);
		}

		if (props.selfCatalogFilters?.filter_by_sync_source) {
			newTags = newTags.concat(
				constructTags(
					props.selfCatalogFilters.filter_by_sync_source,
					[],
					FilterTagType.SelfSyncSource,
					true
				)
			);
		}

		if (props.currentItemSource === ProductSource.CustomDistributor) {
			newTags = newTags.concat(
				constructTags(
					props.customDistributorsFilters ?? [],
					customDistributors?.map(distributor => ({
						label: distributor.integration_name,
						value: distributor.id,
						count: 0
					})) ?? [],
					FilterTagType.CustomDistributor
				)
			);
		}

		setFilterTags(newTags);
		setFilterCount(newTags.length);
	}, [
		props.manufacturerFilters,
		props.categoryFilters,
		props.featureFilters,
		props.metaInfo.manufacturers,
		props.metaInfo.categories,
		featureList,
		props.selfCatalogFilters,
		props.customDistributorsFilters
	]);

	const fetchSuppliers = debounce(async (value?: string) => {
		try {
			let response: SupplierResponse;
			if (value) {
				const supplierRequest: SupplierRequest = {
					limit: 50,
					keywords: value ?? null
				};

				response = await searchSupplier(supplierRequest);
			} else {
				response = (await getSuppliers()) as SupplierResponse;
			}

			const options: IOptionProps[] = response.data.map(option => ({
				name: option.name,
				value: option.id
			}));

			setSupplierOptions(options);
		} catch (error) {
			console.error(error);
		}
	}, 300);

	useEffect(() => {
		setFilterCount(selectedFiltersCount());
	}, [selectedManufacturers, selectedCategories, selectedFeatures]);

	const handleSelfCatalogFilterChange =
		(value: string[], option?: IOptionProps[]) => (key: string) => {
			let newFilters: SelfCatalogFilters = {
				...selfCatalogFilters,
				[key]: value
			};

			if (!!!value.length) {
				const updatedFilters = omit(newFilters, key);
				newFilters = {
					...updatedFilters
				};
			}

			if (key === FilterTagType.SelfSupplier) {
				const selectedSupplierOptionId = [...value].pop();
				const isSelectedSupplierOptionIdAlreadyExists =
					selectedSuppliers.find(
						option => option.value === selectedSupplierOptionId
					);
				if (
					selectedSupplierOptionId &&
					!!!isSelectedSupplierOptionIdAlreadyExists
				) {
					const lastSelectedSupplier = option?.find(
						option => option.value === selectedSupplierOptionId
					) as IOptionProps;
					setSelectedSuppliers([
						...selectedSuppliers,
						lastSelectedSupplier
					]);
				} else {
					setSelectedSuppliers(option as IOptionProps[]);
				}
			}

			setSelfCatalogFilters(newFilters);
		};

	useEffect(() => {
		if (!!selectedCategories?.length || !!selectedManufacturers?.length) {
			shouldUpdateFeatureList.current = true;
		}
	}, [selectedCategories, selectedManufacturers]);

	useEffect(() => {
		setVisible(false);
	}, [props.currentItemSource]);

	useEffect(() => {
		if (
			!visible &&
			props.currentItemSource === ProductSource.Self &&
			props.selfCatalogFilters
		) {
			setSelfCatalogFilters(props.selfCatalogFilters);
		}

		if (visible && props.currentItemSource === ProductSource.Self) {
			fetchSuppliers();
		}
	}, [visible]);

	const sortingOptionsFlag = useFeatureFlag("product_search_sorting");
	const shouldShowSortingOptions =
		(props.currentItemSource === ProductSource.Self ||
			props.currentItemSource === ProductSource.Icecat) &&
		sortingOptionsFlag;

	const handleSortingOptionChange = (value: SortOptions) => {
		props.handleSortingOptionChange?.(value);
	};
	const isDistributorsFilterEnabled =
		selectedOption === FilterOptions.Distributors;
	const EtilizeFiltersPopover = (
		<Popover
			trigger="click"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			data-testid="add-filter-popover"
			content={
				<ZSpin spinning={loading}>
					<Row className="item-filter">
						<Row className="main-menu">
							<ZMenu
								onClick={(event: any) =>
									onMainMenuChange(event.key as FilterOptions)
								}
								selectedKeys={[selectedOption.toString()]}
							>
								<ZMenu.Item key="Manufacturers">
									Manufacturers
								</ZMenu.Item>
								<ZMenu.Item key="Categories">
									Categories
								</ZMenu.Item>
								{!isIcecatIntegrationEnabled && (
									<ZMenu.Item key="Features">
										Features
									</ZMenu.Item>
								)}
							</ZMenu>
							<div className="flex flex-col justify-end mb-2 items-end">
								<ZCheckbox
									checked={shouldFilterByConnectedDistributor}
									onChange={
										updateShouldFilterByConnectedDistributor
									}
									className={`limit-to-distributor pl-4 pb-1 ${
										isDistributorsFilterEnabled
											? "bg-gray-3"
											: ""
									}`}
									disabled={
										!distributorsIntegrated ||
										props.disableLimitDistributorOption
									}
								>
									<div
										className="flex items-center"
										onClick={event => {
											if (
												props.isCDSearchEnhancementEnabled
											) {
												onMainMenuChange(
													FilterOptions.Distributors
												);
												event.stopPropagation();
												event.preventDefault();
											}
										}}
									>
										<ZTypography.ZText
											color={
												isDistributorsFilterEnabled
													? EColors.BLUE
													: EColors.GRAY
											}
											colorVariant={
												isDistributorsFilterEnabled
													? 6
													: 9
											}
										>
											Limit results to my distributors
										</ZTypography.ZText>
										{props.isCDSearchEnhancementEnabled && (
											<ZIcon
												size="md"
												name={EIcons.CHEVRONRIGHT}
												color={
													isDistributorsFilterEnabled
														? EColors.BLUE
														: EColors.GRAY
												}
												colorVariant={
													isDistributorsFilterEnabled
														? 6
														: 9
												}
											/>
										)}
									</div>
								</ZCheckbox>
								<ZCheckbox
									checked={excludeOutOfStock}
									onChange={updateExcludeOutOfStock}
									className="exclude-out-of-stock pl-4"
									disabled={
										!shouldFilterByConnectedDistributor
									}
								>
									Exclude Out of stock
								</ZCheckbox>
								<ZCheckbox
									checked={excludeDropShipWH}
									onChange={updateExcludeDropShipWH}
									className="exclude-drop-ship-wh pl-4"
									disabled={excludeOutofStockDisabled}
								>
									<ZTooltip
										title={
											"Option only available when integrated with Techdata or Synnex"
										}
										showtooltip
										theme="dark"
									>
										<span>Exclude DropShip WH</span>
									</ZTooltip>
								</ZCheckbox>
							</div>
						</Row>
						<Row className="sub-main-wrap">
							{selectedOption === FilterOptions.Distributors ? (
								<div className="h-full w-full px-5 overflow-y-auto">
									<ZTypography.ZText
										colorVariant={9}
										className="mb-2 inline-block"
									>
										System Distributors
									</ZTypography.ZText>
									{connectedSysDistributors.length > 0 ? (
										<ZCheckboxGroup
											spaceClassName="ml-5 gap-3"
											space="middle"
											vertical
											options={connectedSysDistributors.map(
												distributor => ({
													label: distributorLabel[
														distributor
													],
													value: distributor
												})
											)}
											value={
												selectedDistributorsV2.system
											}
											onChange={checkedValues => {
												const stringValues =
													checkedValues.map(a =>
														a.toString()
													);
												setSelectedDistributorsV2({
													...selectedDistributorsV2,
													system: stringValues
												});
											}}
										/>
									) : (
										<ZTypography.ZText
											colorVariant={7}
											className="inline-block"
										>
											No System Distributors connected
										</ZTypography.ZText>
									)}
									<ZTypography.ZText
										colorVariant={9}
										className="mb-2 mt-5 inline-block"
									>
										Custom Distributors
									</ZTypography.ZText>
									{Boolean(customDistributors?.length) ? (
										<ZCheckboxGroup
											spaceClassName="ml-5 gap-3"
											space="middle"
											vertical
											options={customDistributors?.map(
												distributor => ({
													label: distributor.integration_name,
													value: distributor.id
												})
											)}
											value={
												selectedDistributorsV2.custom
											}
											onChange={checkedValues => {
												const stringValues =
													checkedValues.map(a =>
														a.toString()
													);
												setSelectedDistributorsV2({
													...selectedDistributorsV2,
													custom: stringValues
												});
											}}
										/>
									) : (
										<ZTypography.ZText
											colorVariant={7}
											className="inline-block"
										>
											No Custom Distributors connected
										</ZTypography.ZText>
									)}
								</div>
							) : selectedOption === FilterOptions.Features ? (
								<FeatureCollapse
									allFeatures={featureList}
									selectedFeatures={selectedFeatures}
									selectedFeaturesRef={props.featureFilters}
									onFeatureSelectDeselect={
										onFeatureSelectDeselect
									}
								/>
							) : (
								<ZCheckboxGroup
									spaceClassName="ml-6 mr-5"
									space="middle"
									vertical
									options={
										selectedOption ===
										FilterOptions.Manufacturers
											? manufacturerList
											: categoryList
									}
									value={
										selectedOption ===
										FilterOptions.Manufacturers
											? selectedManufacturers
											: selectedCategories
									}
									onChange={e => {
										onFilterSelectDeselect(
											selectedOption,
											e
										);
									}}
								/>
							)}
						</Row>
						<div className="footer-wrap">
							{filterCount ? (
								<span className="filters-count-placeholder">
									<span
										className="filters-count"
										data-testid="filter-count"
									>{`${filterCount} Filter(s) selected`}</span>
									<CloseOutlined
										onClick={handleRemoveAllFilters}
									/>
								</span>
							) : (
								<span></span>
							)}
							<span></span> {/* To align items*/}
							<ZButton
								onClick={() => handleVisibleChange(false)}
								type="default"
							>
								Cancel
							</ZButton>
							<ZButton onClick={onApply} type="primary">
								Apply
							</ZButton>
						</div>
					</Row>
				</ZSpin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</Popover>
	);

	const supplierSelectProps = {
		onSearch: (searchValue: string) => fetchSuppliers(searchValue),
		onBlur: () => fetchSelectedSuppliersById(),
		selectOptions: supplierOptions
	};

	const SelfFiltersPopover = (
		<Popover
			trigger="click"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			content={
				<ZSpin spinning={loading}>
					<Row className="self-item-filter">
						<Row className="main-menu">
							<div className="filter-container">
								<span className="filter-title">
									Product Type
								</span>
								<div className="filter">
									<Select
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										allowClear
										mode="multiple"
										placeholder="Select product type"
										onChange={(value: string[]) =>
											handleSelfCatalogFilterChange(
												value
											)(
												SelfCatalogFilterKeys.FilterByProductType
											)
										}
										value={
											selfCatalogFilters.filter_by_item_types
										}
									>
										{ProductTypes.map((product, index) => (
											<Select.Option
												key={index}
												value={product}
											>
												{capitalizeFirstLetter(product)}
											</Select.Option>
										))}
									</Select>
								</div>
							</div>
							<div className="filter-container">
								<span className="filter-title">Category</span>
								<div className="filter">
									<Select
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										allowClear
										dropdownClassName="display-none"
										mode="tags"
										placeholder="Enter category"
										onChange={(value: string[]) =>
											handleSelfCatalogFilterChange(
												value
											)(
												SelfCatalogFilterKeys.FilterByCategory
											)
										}
										value={
											selfCatalogFilters.filter_by_categories
										}
									/>
								</div>
							</div>
							<div className="filter-container">
								<span className="filter-title">Supplier</span>
								<div className="filter">
									<ZSelect
										{...supplierSelectProps}
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										allowClear
										mode="multiple"
										filterOption={filterSelectByTextInput}
										optionFilterProp="name"
										showSearch
										placeholder="Enter supplier"
										onChange={(value: string[], option) =>
											handleSelfCatalogFilterChange(
												value,
												option as IOptionProps[]
											)(
												SelfCatalogFilterKeys.FilterBySupplierIds
											)
										}
										value={
											selfCatalogFilters.filter_by_supplier_ids
										}
									/>
								</div>
							</div>
							<div className="filter-container">
								<span className="filter-title">
									Manufacturer
								</span>
								<div className="filter">
									<Select
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										allowClear
										dropdownClassName="display-none"
										mode="tags"
										placeholder="Enter manufacturer"
										onChange={(value: string[]) =>
											handleSelfCatalogFilterChange(
												value
											)(
												SelfCatalogFilterKeys.FilterByManufacturers
											)
										}
										value={
											selfCatalogFilters.filter_by_manufacturers
										}
									/>
								</div>
							</div>
							<div className="filter-container">
								<span className="filter-title">
									Sync source
								</span>
								<div className="filter">
									<Select
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										allowClear
										mode="multiple"
										placeholder="Select sync source"
										onChange={(value: string[]) =>
											handleSelfCatalogFilterChange(
												value
											)(
												SelfCatalogFilterKeys.FilterBySyncSource
											)
										}
										value={
											selfCatalogFilters.filter_by_sync_source
										}
									>
										{[
											...ZomentumPSAsArray,
											"dell_quote_import",
											"data_import",
											...(isIcecatIntegrationEnabled
												? [ProductSource.Icecat]
												: [
														ProductSource.Etilize,
														ProductSource.Icecat
												  ]),
											"zapier",
											ProductSource.Amazon
										].map((psa, index) => (
											<Select.Option
												value={
													psa === "infusion_soft"
														? "infusionsoft"
														: psa
												}
												key={index}
											>
												{capitalizeAfterUnderscore(
													psa ===
														ProductSource.Icecat &&
														isIcecatIntegrationEnabled
														? "Global Catalogue"
														: zomentumPSALabel[psa]
														? zomentumPSALabel[psa]
														: psa
												)}
											</Select.Option>
										))}
									</Select>
								</div>
							</div>
						</Row>
						<div className="mt-3 w-39 ml-auto flex justify-between">
							<ZButton
								onClick={handleRemoveAllFilters}
								type="default"
							>
								Clear
							</ZButton>
							<ZButton onClick={onApply} type="primary">
								Apply
							</ZButton>
						</div>
					</Row>
				</ZSpin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</Popover>
	);

	const MarketplaceSkuPopover = (
		<Popover
			trigger="click"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			content={
				<Spin spinning={loading}>
					<Row className="self-item-filter">
						<Col span={24}>
							<Row className="main-menu">
								<div className="filter-container">
									<span className="filter-title">
										Product Type
									</span>
									<div className="filter">
										<Select
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											allowClear
											mode="multiple"
											placeholder="Select product type"
											onChange={(value: string[]) =>
												handleSelfCatalogFilterChange(
													value
												)(
													SelfCatalogFilterKeys.FilterByProductType
												)
											}
											value={
												selfCatalogFilters.filter_by_item_types
											}
										>
											{ProductTypes.map(
												(product, index) => (
													<Select.Option
														key={index}
														value={product}
													>
														{capitalizeFirstLetter(
															product
														)}
													</Select.Option>
												)
											)}
										</Select>
									</div>
								</div>
								<div className="filter-container">
									<span className="filter-title">Vendor</span>
									<div className="filter">
										<Select
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											allowClear
											dropdownClassName="display-none"
											mode="tags"
											placeholder="Enter vendor"
											onChange={(value: string[]) =>
												handleSelfCatalogFilterChange(
													value
												)(
													SelfCatalogFilterKeys.FilterByVendors
												)
											}
											value={
												selfCatalogFilters.filter_by_vendors
											}
										/>
									</div>
								</div>
							</Row>
						</Col>
						<div className="footer-wrap">
							<ZButton
								onClick={handleRemoveAllFilters}
								type="default"
							>
								Clear
							</ZButton>
							<ZButton onClick={onApply} type="primary">
								Apply
							</ZButton>
						</div>
					</Row>
				</Spin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</Popover>
	);

	const handleAmazonFilterChange = (value: string[]) => (key: string) => {
		const selectedValue = !!value[0] ? value : [];
		if (key === AmazonCatalogFilterKeys.FilterByProductCategory) {
			setAmazonFiltersState({
				...amazonFiltersState,
				amazonProductCategory: selectedValue
			});
		}
		if (key === AmazonCatalogFilterKeys.FilterBySubCategory) {
			setAmazonFiltersState({
				...amazonFiltersState,
				amazonProductSubCategory: selectedValue
			});
		}
		if (key === AmazonCatalogFilterKeys.FilterByDeliveryDay) {
			setAmazonFiltersState({
				...amazonFiltersState,
				amazonProductDeliveryDay: selectedValue
			});
		}
		if (key === AmazonCatalogFilterKeys.FilterByAvailability) {
			setAmazonFiltersState({
				...amazonFiltersState,
				includeOutOfStockProducts: selectedValue
			});
		}
	};

	const updateFreeShippingFilter = (e: CheckboxChangeEvent) => {
		setAmazonFiltersState({
			...amazonFiltersState,
			eligibleForFreeShipping: e.target.checked
		});
	};

	const updatePrimeEligibleFilter = (e: CheckboxChangeEvent) => {
		setAmazonFiltersState({
			...amazonFiltersState,
			amazonPrimeEligible: e.target.checked
		});
	};

	const amazonRefineMentsFilters = props.metaInfo.amazon_refinements;

	const AmazonFilterPopover = (
		<ZPopover
			trigger="click"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			size="sm"
			content={
				<ZSpin spinning={loading}>
					<Row className="amazon-item-filter">
						<Row className="main-menu">
							<div className="filter-container">
								<span className="filter-title">
									Product Category
								</span>
								<div className="filter amazon-filter">
									<ZSelect
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										placeholder="Select Product Category"
										options={
											props.metaInfo.categories?.length
												? props.metaInfo.categories
												: amazonAppliedFilters?.categories
										}
										value={
											amazonFiltersState.amazonProductCategory
										}
										onChange={(value: string[]) =>
											handleAmazonFilterChange(
												[value].flat()
											)(
												AmazonCatalogFilterKeys.FilterByProductCategory
											)
										}
										allowClear
									/>
								</div>
							</div>
							{(!!props.metaInfo.sub_categories?.length ||
								!!amazonAppliedFilters?.sub_categories
									?.length) && (
								<div className="filter-container">
									<span className="filter-title">
										Sub Category
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter Sub Category"
											options={
												props.metaInfo.sub_categories
													?.length
													? props.metaInfo
															.sub_categories
													: amazonAppliedFilters?.sub_categories
											}
											value={
												amazonFiltersState.amazonProductSubCategory
											}
											onChange={(value: string[]) =>
												handleAmazonFilterChange(
													[value].flat()
												)(
													AmazonCatalogFilterKeys.FilterBySubCategory
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}
							{!!amazonRefineMentsFilters?.delivery_day_options
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Delivery Day
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter delivery day"
											options={
												amazonRefineMentsFilters?.delivery_day_options ??
												[]
											}
											value={
												amazonFiltersState.amazonProductDeliveryDay
											}
											onChange={(value: string[]) =>
												handleAmazonFilterChange(
													[value].flat()
												)(
													AmazonCatalogFilterKeys.FilterByDeliveryDay
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}
							{(!!amazonRefineMentsFilters?.availability_options
								?.length ||
								!!amazonAppliedFilters?.availability_options
									?.length) && (
								<div className="filter-container">
									<span className="filter-title">
										Availability
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter availability"
											options={
												amazonRefineMentsFilters
													?.availability_options
													?.length
													? amazonRefineMentsFilters?.availability_options
													: amazonAppliedFilters?.availability_options
											}
											value={
												amazonFiltersState.includeOutOfStockProducts
											}
											onChange={(value: string[]) =>
												handleAmazonFilterChange(
													[value].flat()
												)(
													AmazonCatalogFilterKeys.FilterByAvailability
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}
							{!!amazonRefineMentsFilters
								?.eligible_for_free_shipping_options
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Shipping
									</span>
									<div className="filter amazon-filter">
										<ZCheckbox
											checked={
												amazonFiltersState.eligibleForFreeShipping
											}
											onChange={updateFreeShippingFilter}
											className="limit-to-distributor"
										>
											Eligible For Free Shipping
										</ZCheckbox>
									</div>
								</div>
							)}
							{!!amazonRefineMentsFilters?.prime_eligible
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Prime Eligible
									</span>
									<div className="filter amazon-filter">
										<ZCheckbox
											checked={
												amazonFiltersState.amazonPrimeEligible
											}
											onChange={updatePrimeEligibleFilter}
											className="limit-to-distributor"
										>
											<ZImage
												width={57}
												height={16}
												preview={false}
												alt="Prime Tag"
												wrapperClassName="pt-0.5"
												src={amazonPrimeLogo}
											/>
										</ZCheckbox>
									</div>
								</div>
							)}
						</Row>
						<div className="mt-3 w-39 ml-auto flex justify-between">
							<ZButton
								onClick={handleRemoveAllFilters}
								type="default"
							>
								Clear
							</ZButton>
							<ZButton onClick={onApply} type="primary">
								Apply
							</ZButton>
						</div>
					</Row>
				</ZSpin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</ZPopover>
	);

	const CustomDistributorsPopover = (
		<ZPopover
			trigger="click"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			size="sm"
			content={
				<ZSpin spinning={loading}>
					<div>
						<div className="px-4 py-3">
							<ZTypography.ZTitle level={2} className="mb-2">
								Custom Distributors
							</ZTypography.ZTitle>
							<ZCheckboxGroup
								vertical
								options={customDistributors?.map(
									distributor => ({
										label: distributor.integration_name,
										value: distributor.id
									})
								)}
								value={selectedCustomDistributors}
								onChange={(e: CheckboxValueType[]) => {
									setSelectedCustomDistributors(
										e as string[]
									);
								}}
							/>
						</div>
						<Divider />
						<div className="px-4 py-3">
							<ZButton
								onClick={() => handleVisibleChange(false)}
								type="default"
								className="mr-2"
							>
								Cancel
							</ZButton>
							<ZButton
								onClick={onApply}
								type="primary"
								disabled={!selectedCustomDistributors.length}
							>
								Apply
							</ZButton>
						</div>
					</div>
				</ZSpin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</ZPopover>
	);

	let AddFilterPopover = EtilizeFiltersPopover;
	if (!props.isFromProductsPage || props.itemType === ProductType.Bundle) {
		if (props.currentItemSource === ProductSource.Self) {
			AddFilterPopover = SelfFiltersPopover;
		} else if (
			props.currentItemSource === ProductSource.ZomentumMarketplace
		) {
			AddFilterPopover = MarketplaceSkuPopover;
		}
	}
	if (props.currentItemSource === ProductSource.Amazon) {
		AddFilterPopover = AmazonFilterPopover;
	}
	if (props.currentItemSource === ProductSource.CustomDistributor) {
		AddFilterPopover = CustomDistributorsPopover;
	}

	return (
		<div ref={containerRef} className="item-filters-menu">
			<Row>
				<Col span={5}>{AddFilterPopover}</Col>
				<Col span={14}>
					<FilterTags tagData={filterTags} onTagClose={onTagClose} />
				</Col>
				<Col span={5}>
					{shouldShowSortingOptions && (
						<ZSelect
							size="small"
							onChange={handleSortingOptionChange}
							placeholder={"Sort Products by"}
							allowClear={true}
							selectOptions={SortingOptions}
							className="w-full float-right"
						/>
					)}
				</Col>
			</Row>
		</div>
	);
};

const FeatureCollapse = (props: {
	allFeatures: ProductFeature[];
	selectedFeatures: FeatureFilter[];
	selectedFeaturesRef: FeatureFilter[];
	onFeatureSelectDeselect: (
		values: CheckboxValueType[],
		featId: string
	) => void;
}) => {
	const [featuresToShow, setFeaturesToShow] = useState<ProductFeature[]>([]);

	const shouldShowShowMoreButton = (feat: ProductFeature) => {
		const actualFeat = props.allFeatures.find(
			fullFeat => fullFeat.value === feat.value
		);
		return actualFeat
			? actualFeat?.children?.length > feat?.children?.length
			: false;
	};

	const handleShowMoreClick = (feat: ProductFeature) => {
		const actualFeat = props.allFeatures.find(
			fullFeat => fullFeat.value === feat.value
		);
		const featToShow = featuresToShow.find(
			showFeat => showFeat.value === feat.value
		);
		if (actualFeat && featToShow) {
			const shownOptions = featToShow.children;
			const notShownOptions = actualFeat.children.filter(
				feat =>
					!!!shownOptions.find(shown => shown.value === feat.value)
			);
			const noOfOptionsToShow = shownOptions.length + 10;
			const newOptions = [...shownOptions, ...notShownOptions];
			const newOptionsToShow =
				newOptions.length > noOfOptionsToShow
					? newOptions?.slice(0, noOfOptionsToShow)
					: newOptions;
			featToShow.children = newOptionsToShow;
			setFeaturesToShow([...featuresToShow]);
		}
	};

	useEffect(() => {
		// Selected features will be showed at the top
		const reorderFeatures = () => {
			return props.allFeatures.sort((a, b) => {
				const isASelected =
					(props.selectedFeaturesRef.find(
						selected => a.value === selected.id
					)?.values?.length || 0) > 0;

				const isBSelected =
					(props.selectedFeaturesRef.find(
						selected => b.value === selected.id
					)?.values?.length || 0) > 0;
				// To maintain the order of the selected, not selected features
				return isBSelected ? 1 : isASelected ? -1 : 0;
			});
		};

		const constructFeatureList = () => {
			const noOfItemsToDisplay = 5;
			const featList = reorderFeatures().map(feat => {
				const selected =
					props.selectedFeaturesRef.find(sel => sel.id === feat.value)
						?.values || [];
				const selectedOptions = selected
					.map(id =>
						feat.children.filter(child => child.value === id)
					)
					.reduce((a, b) => a.concat(b), []);

				const notSelectedOptions = feat.children.filter(
					child =>
						!!!selectedOptions.find(selected => selected === child)
				);
				const allOptions = [...selectedOptions, ...notSelectedOptions];
				const optionsToShow =
					allOptions?.length > noOfItemsToDisplay
						? allOptions?.slice(0, noOfItemsToDisplay)
						: allOptions;
				const withUpdatedCountInLabel = optionsToShow.map(pair => {
					const newPair = new ProductKeyValuePair();
					newPair.label = `${pair.label} (${pair.count})`;
					newPair.value = pair.value;
					newPair.count = pair.count;
					return newPair;
				});
				const newFeat = new ProductFeature();
				newFeat.value = feat.value;
				newFeat.label = `${feat.label} (${feat.count})`;
				newFeat.count = feat.count;
				newFeat.children = withUpdatedCountInLabel;
				return newFeat;
			});
			return featList;
		};
		setFeaturesToShow(constructFeatureList());
	}, [props.allFeatures, props.selectedFeaturesRef]);

	return (
		<Collapse bordered={false}>
			{featuresToShow.map(feat => {
				const selected =
					props.selectedFeatures.find(sf => sf.id === feat.value)
						?.values || [];
				const panelLabel =
					selected.length > 0 ? `${feat.label}*` : feat.label;
				return (
					<Panel header={panelLabel} key={feat.value}>
						<FeaturePanel
							feat={feat}
							selected={selected}
							shouldShowShowMoreButton={shouldShowShowMoreButton(
								feat
							)}
							onFeatureSelectDeselect={
								props.onFeatureSelectDeselect
							}
							handleShowMoreClick={handleShowMoreClick}
						/>
					</Panel>
				);
			})}
		</Collapse>
	);
};

const FeaturePanel = (props: {
	feat: ProductFeature;
	selected: string[];
	shouldShowShowMoreButton: boolean;
	onFeatureSelectDeselect: (
		values: CheckboxValueType[],
		featId: string
	) => void;
	handleShowMoreClick: (feat: ProductFeature) => void;
}) => {
	return (
		<Row>
			<Col span={24}>
				<ZCheckboxGroup
					spaceClassName="my-3 ml-3 mr-2"
					space="middle"
					vertical
					options={props.feat.children}
					value={props.selected}
					onChange={values =>
						props.onFeatureSelectDeselect(values, props.feat.value)
					}
				/>
			</Col>
			<Col span={24}>
				{props.shouldShowShowMoreButton && (
					<ZButton
						type="link"
						onClick={() => props.handleShowMoreClick(props.feat)}
					>
						Show more
					</ZButton>
				)}
			</Col>
		</Row>
	);
};

export const FilterTags = (props: {
	tagData: FilterTagData[];
	onTagClose: (
		type: FilterTagType,
		filterId: string,
		featChildId?: string
	) => void;
}) => {
	const containerRef = useRef<HTMLDivElement>(null);

	const filterTags = useMemo(() => {
		const noOfTags = 10;
		return props.tagData.length > noOfTags
			? {
					toShow: props.tagData.slice(0, noOfTags),
					others: props.tagData.slice(noOfTags, props.tagData.length)
			  }
			: { toShow: props.tagData, others: [] };
	}, [props.tagData]);

	const constructTagElements = (tagData: FilterTagData[]) => {
		return tagData.map(filterTag => {
			const key = `${filterTag.id}:${
				filterTag.featChildId ? filterTag.featChildId : filterTag.type
			}`;
			const toDisplay = (
				<span className="item-filter-tag-data">{`${filterTag.name}${
					filterTag.type === FilterTagType.Feat
						? " - " + filterTag.featChildName
						: ""
				}`}</span>
			);
			return (
				<Tag
					key={key}
					closable={true}
					onClose={() =>
						props.onTagClose(
							filterTag.type,
							filterTag.id,
							filterTag.featChildId
						)
					}
					data-testid="filter-tag"
				>
					{toDisplay}
				</Tag>
			);
		});
	};

	return (
		<div ref={containerRef}>
			<Col offset={1} span={19} className="item-filter-tag">
				{constructTagElements(filterTags.toShow)}
				{!!filterTags?.others?.length && (
					<Popover
						content={
							<Row>
								{filterTags.others.map(filterTag => {
									const toShow = `${filterTag.name}${
										filterTag.type === FilterTagType.Feat
											? " " + filterTag.featChildName
											: ""
									}`;
									return (
										<Col
											span={24}
											key={`${filterTag.id} ${filterTag.featChildId}:${filterTag.type}`}
										>
											<Row className="filter-text">
												<Col
													span={22}
													className="filter-inner-text"
												>
													{toShow}
												</Col>
												<Col
													span={1}
													onClick={() =>
														props.onTagClose(
															filterTag.type,
															filterTag.id,
															filterTag.featChildId
														)
													}
												>
													<CloseOutlined />
												</Col>
											</Row>
										</Col>
									);
								})}
							</Row>
						}
						trigger="click"
						overlayClassName="item-more-filters-popover"
						getPopupContainer={trigger =>
							containerRef.current
								? (containerRef.current as HTMLElement)
								: (trigger as HTMLElement)
						}
						placement="bottomLeft"
					>
						<span
							data-testid="more-filter-btn"
							className="filter-show-more-button"
						>{`${filterTags.others.length} more`}</span>
					</Popover>
				)}
			</Col>
		</div>
	);
};

export default ItemFiltersMenu;
